/**
 * Shared gist.is web worker-session cookie helpers.
 *
 * Plan 212 introduced HttpOnly `gist_session` for SSR and API cookie auth.
 * Every web surface that signs users in or out must mint or clear that cookie
 * via POST /api/auth/session and POST /api/auth/logout — not only `/join`.
 *
 * Called by:
 * - `join.ts`, `options.ts`, `help-contact.ts`, `try-auth.ts`
 *
 * @file src/client/web-session-cookie.ts
 */

export const WEB_SESSION_URL = '/api/auth/session';
export const WEB_LOGOUT_URL = '/api/auth/logout';

/** Client version strings for `X-Gist-Client-Version` on web session routes. */
export type WebSessionClientSurface = 'web-join' | 'web-options' | 'web-help' | 'web-try';

/**
 * Protocol headers required by `withVersionCheck` on `/api/*` routes.
 */
export function buildWebProtocolHeaders(clientVersion: WebSessionClientSurface): Record<string, string> {
  return {
    'X-Gist-Protocol-Version': '2',
    'X-Gist-Client-Version': clientVersion
  };
}

/**
 * Clears the HttpOnly worker session cookie and deletes KV `worker-session:<uid>`.
 *
 * Always sends `credentials: 'include'` so cookie-only SSR identity is cleared
 * even when no Bearer token is available (for example after Firebase sign-out).
 */
export async function clearWebWorkerSession(args: {
  authorization?: string | null;
  clientVersion: WebSessionClientSurface;
  logPrefix?: string;
}): Promise<void> {
  const logPrefix = args.logPrefix ?? '[gist-web-session]';
  const headers: Record<string, string> = {
    ...buildWebProtocolHeaders(args.clientVersion)
  };
  if (args.authorization) {
    headers.Authorization = args.authorization;
  }

  try {
    await fetch(WEB_LOGOUT_URL, {
      method: 'POST',
      credentials: 'include',
      headers
    });
  } catch (error) {
    console.warn(`${logPrefix} Server logout failed`, error);
  }
}

/**
 * Exchanges a Bearer token (Firebase id token or worker token) for a worker
 * session and sets `gist_session`. When `authorization` is omitted, the request
 * still sends `credentials: 'include'` so an existing `gist_session` can refresh.
 */
export async function mintWebWorkerSession<T = unknown>(args: {
  authorization?: string | null;
  clientVersion: WebSessionClientSurface;
  logPrefix?: string;
}): Promise<T | null> {
  const logPrefix = args.logPrefix ?? '[gist-web-session]';
  const headers: Record<string, string> = {
    ...buildWebProtocolHeaders(args.clientVersion)
  };
  if (args.authorization) {
    headers.Authorization = args.authorization;
  }
  try {
    const response = await fetch(WEB_SESSION_URL, {
      method: 'POST',
      credentials: 'include',
      headers
    });
    if (!response.ok) {
      console.warn(`${logPrefix} Session mint failed`, response.status);
      return null;
    }
    return (await response.json()) as T;
  } catch (error) {
    console.warn(`${logPrefix} Session mint threw`, error);
    return null;
  }
}
